import type { DataTableColumn } from '@sparklayer-io/spark-dash-ui/components/ui/data-table/types';
import { DataTable, Text } from '@ui/themed';
import { t } from 'i18next';
import type { Platform } from '../../../context/env-context';
import { I18N_COMPONENTS } from '../constants';
import { findImage, findOptions, findVariantImage } from '../product-metadata';
import type { ProductParentRow, ProductSearchRow, ProductVariantRow } from '../product-search-rows';
import { ProductImage } from './product-image';

export function productSearchColumns({
  platform,
}: {
  platform?: Platform;
}): DataTableColumn<ProductSearchRow>[] {
  return [
    {
      id: 'product',
      label: t(`${I18N_COMPONENTS}.product_search.headings.product`),
      renderCell: (row) =>
        row.kind === 'parent' ? (
          <ParentCell row={row} platform={platform} />
        ) : (
          <VariantCell row={row} platform={platform} />
        ),
    },
    {
      id: 'identifier',
      label: t(`${I18N_COMPONENTS}.product_search.headings.identifier`),
      renderCell: (row) => (
        <span data-testid={row.kind === 'parent' ? 'parent-product-slug' : 'variant-sku'}>
          <Text variant="secondary" truncate>
            {row.kind === 'parent' ? row.product.slug : row.variant.identifiers.sku}
          </Text>
        </span>
      ),
    },
  ];
}

function ParentCell({ row, platform }: { row: ProductParentRow; platform?: Platform }) {
  const image = findImage(row.product, platform);

  return (
    <div className="flex items-start gap-3">
      <ProductImage
        src={image}
        size="md"
        label={t(`${I18N_COMPONENTS}.product_search.parent_image`)}
        emptyLabel={t(`${I18N_COMPONENTS}.product_search.parent_image_unavailable`)}
      />
      <div className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-1">
          <span data-testid="parent-product-title">
            <Text bold truncate>
              {row.product.title}
            </Text>
          </span>
          <DataTable.Expander />
        </span>
        {row.children.length > 1 && (
          <span className="text-gray-500 text-xs" data-testid="parent-product-variant-count">
            {t(`${I18N_COMPONENTS}.product_search.parent_variant_count`, {
              count: row.children.length,
            })}
          </span>
        )}
      </div>
    </div>
  );
}

function VariantCell({ row, platform }: { row: ProductVariantRow; platform?: Platform }) {
  const image = findVariantImage(row.variant, platform);

  return (
    <div className="flex items-start gap-3">
      <ProductImage
        src={image}
        size="sm"
        label={t(`${I18N_COMPONENTS}.product_search.variant_image`)}
        emptyLabel={t(`${I18N_COMPONENTS}.product_search.variant_image_unavailable`)}
      />
      <div className="flex min-w-0 flex-col">
        <span data-testid="variant-options">
          <Text truncate>{findOptions(row.variant)}</Text>
        </span>
      </div>
    </div>
  );
}
